interface Campaign {
  id?: string;
  _id?: string;
  [key: string]: any; // Allow for dynamic properties
}

interface Collection {
  id: number ;
  name: string;
}

interface CampaignsResponse {
  collection: string;
  data: Campaign[];
}

interface CollectionsResponse {
  collections: Collection[];
}


  // Base API URL
const API_BASE_URL = "https://interim-cabdemo-module.ispgnet.com";



// Common fetch helper with error handling
async function fetchWithErrorHandling<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `API error: ${response.status}`);
  }

  return response.json();
}

// Fetch collections for dropdown using the provided API endpoint
export async function fetchCollections(): Promise<Collection[]> {
  try {
    const response: CollectionsResponse = await fetchWithErrorHandling<CollectionsResponse>(`${API_BASE_URL}/fetch_collections/`);
    return response.collections || [];
  } catch (error) {
    console.error("Error fetching collections:", error);
    // Fallback to mock data in case the API isn't available yet
    return [
      { id: 1, name: "Initial" },
      { id: 2, name: "Testing" },
    ];
  }
}



// Fetch campaigns with filtering/pagination
interface FetchCampaignsParams {
  collection?: string | number;
  page?: number;
  limit?: number;
  search?: string;
}

// Fetch campaigns with correct collection name
export async function fetchCampaigns(params: FetchCampaignsParams = {}): Promise<CampaignsResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    // Fetch collections to get correct name
    const collections = await fetchCollections();
    const collectionObj = collections.find(col => col.id.toString() === params.collection?.toString());
    
    if (!collectionObj) {
      throw new Error(`Collection '${params.collection}' not found. Available: ${collections.map(c => c.name).join(", ")}`);
    }
    
    // Append collection name (not ID)
    queryParams.append('collection', collectionObj.name);
    
    const response = await fetchWithErrorHandling<CampaignsResponse>(
      `${API_BASE_URL}/fetch_data/?${queryParams.toString()}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching campaigns:", error);
    
    // Fallback Mock Data
    return {
      collection: params.collection?.toString() || "Testing",
      data: []
    };
  }
}

// Retrieve customer service details based on date range
interface RetrieveCustomerServiceParams {
  fromDate: string;
  toDate: string;
  result_collection: string;
}

interface CustomerServiceResponse {
  message: string;
  count: number;
}

export async function retrieveCustomerServiceDetails(params: RetrieveCustomerServiceParams): Promise<CustomerServiceResponse> {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('fromDate', params.fromDate);
    queryParams.append('toDate', params.toDate);
    queryParams.append('result_collection', params.result_collection);
    
    return await fetchWithErrorHandling<CustomerServiceResponse>(
      `${API_BASE_URL}/retrieve_customer_service_details/?${queryParams.toString()}`
    );
  } catch (error) {
    console.error("Error retrieving customer service details:", error);
    throw error;
  }
}


// hooks/api.ts
// export interface CreateCampaign {
//   name: string;
//   collectionId: string;
//   startDate: string;
//   endDate: string;
// }

// export interface CustomerServiceDetails {
//   fromDate: string;
//   toDate: string;
//   resultCollection: string;
// }

// export async function createCampaign(campaign: CreateCampaign): Promise<any> {
//   try {
//     // When creating a campaign, also retrieve customer service details
//     const response = await fetch(`/retrieve_customer_service_details/?fromDate=${campaign.startDate}&toDate=${campaign.endDate}&result_collection=${campaign.collectionId}`, {
//       method: 'GET',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     });
    
//     if (!response.ok) {
//       const errorData = await response.json();
//       throw new Error(errorData.detail || 'Failed to create campaign');
//     }
    
//     return await response.json();
//   } catch (error) {
//     console.error('API Error:', error);
//     throw error;
//   }
// }


export interface CallRequest {
  name: string;
  phone_number: string;
  vehicle_name: string;
  city: string;
  service_center: string;
  due_date: string;
  odometer: string;
  license_plate: string;
  collection_name: string;
  calling_number :string;
}

export interface CallResponse {
  success: boolean;
  call_sid: string;
}

// Make a call through Twilio
export async function makeCall(callData: CallRequest): Promise<CallResponse> {
  try {
    return fetchWithErrorHandling<CallResponse>(`https://pipecat-voice-bot-dev.ispgnet.com/make-call`, {
      method: 'POST',
      body: JSON.stringify(callData),
    });
  } catch (error) {
    console.error("Error making call:", error);
    throw error;
  }
}

export async function makeMultipleCalls(callData: { num_accounts: number; calls_per_account: number; payloads: CallRequest[] }): Promise<{ 
  success: boolean; 
  completedCalls: number; 
  totalCalls: number; 
  status: string; // Add the status property
  total_payloads: number; // Add the total_payloads property
  accounts: any[]; // Add the accounts property
  calls_per_account: number; // Add the calls_per_account property
  }> {
  const totalCalls = callData.payloads.length;

  try {
    // Send all call data in a single API call
    const response = await fetch('https://pipecat-voice-bot-dev.ispgnet.com/process-calls', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(callData),
    });

    if (!response.ok) {
      throw new Error('Failed to make calls');
    }

    const result = await response.json();

    return {
      success: result.completedCalls === totalCalls,
      completedCalls: result.completedCalls,
      totalCalls,
      status: result.status, // Assuming result.status exists
      total_payloads: result.total_payloads, // Assuming result.total_payloads exists
      accounts: result.accounts, // Assuming result.accounts exists
      calls_per_account: callData.calls_per_account, // Assuming callData.calls_per_account exists
    };
  } catch (error) {
  console.error("Error making multiple calls:", error);
    return {
      success: false,
      completedCalls: 0,
      totalCalls,
      status: 'error', 
      total_payloads: 0, // Add a default value for total_payloads
      accounts: [], // Add a default value for accounts
      calls_per_account: 0, // Add a default value for calls_per_account
    };
  }
}
